import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { DEFAULT_FORMULAS, type AuditLogEntry } from './formulaTypes'

interface FormulaStore {
  // Custom formulas (only stores overrides, not defaults)
  customFormulas: Record<string, string>

  // Audit log
  auditLog: AuditLogEntry[]

  // Actions
  getFormula: (key: string) => string
  isFormulaCustom: (key: string) => boolean
  saveFormula: (key: string, formula: string, user: { email: string; name: string }) => void
  resetFormula: (key: string, user: { email: string; name: string }) => void
  resetAllFormulas: (user: { email: string; name: string }) => void
  clearAuditLog: () => void
}

const generateId = () => Math.random().toString(36).substring(2, 15)

export const useFormulaStore = create<FormulaStore>()(
  persist(
    (set, get) => ({
      customFormulas: {},
      auditLog: [],

      getFormula: (key: string) => {
        const { customFormulas } = get()
        return customFormulas[key] || DEFAULT_FORMULAS[key]?.formula || ''
      },

      isFormulaCustom: (key: string) => {
        const { customFormulas } = get()
        return key in customFormulas
      },

      saveFormula: (key: string, formula: string, user: { email: string; name: string }) => {
        const { customFormulas, auditLog } = get()
        const oldFormula = customFormulas[key] || DEFAULT_FORMULAS[key]?.formula || ''

        // Don't save if unchanged
        if (formula === oldFormula) return

        const entry: AuditLogEntry = {
          id: generateId(),
          timestamp: new Date().toISOString(),
          userEmail: user.email,
          userName: user.name,
          formulaKey: key,
          formulaName: DEFAULT_FORMULAS[key]?.name || key,
          action: 'modified',
          oldValue: oldFormula,
          newValue: formula,
        }

        set({
          customFormulas: { ...customFormulas, [key]: formula },
          auditLog: [entry, ...auditLog].slice(0, 100), // Keep last 100 entries
        })
      },

      resetFormula: (key: string, user: { email: string; name: string }) => {
        const { customFormulas, auditLog } = get()

        if (!(key in customFormulas)) return // Nothing to reset

        const oldFormula = customFormulas[key]
        const defaultFormula = DEFAULT_FORMULAS[key]?.formula || ''

        const entry: AuditLogEntry = {
          id: generateId(),
          timestamp: new Date().toISOString(),
          userEmail: user.email,
          userName: user.name,
          formulaKey: key,
          formulaName: DEFAULT_FORMULAS[key]?.name || key,
          action: 'reset',
          oldValue: oldFormula,
          newValue: defaultFormula,
        }

        const newCustomFormulas = { ...customFormulas }
        delete newCustomFormulas[key]

        set({
          customFormulas: newCustomFormulas,
          auditLog: [entry, ...auditLog].slice(0, 100),
        })
      },

      resetAllFormulas: (user: { email: string; name: string }) => {
        const { auditLog } = get()

        const entry: AuditLogEntry = {
          id: generateId(),
          timestamp: new Date().toISOString(),
          userEmail: user.email,
          userName: user.name,
          formulaKey: 'all',
          formulaName: 'All Formulas',
          action: 'reset_all',
          oldValue: 'All custom formulas',
          newValue: 'Default values',
        }

        set({
          customFormulas: {},
          auditLog: [entry, ...auditLog].slice(0, 100),
        })
      },

      clearAuditLog: () => {
        set({ auditLog: [] })
      },
    }),
    {
      name: 'dealhub-formulas',
    }
  )
)
